import type { ImageAspectRatio } from "common/components/image-types";
import { Corners } from "common/components/image-types";
import { isEmptyOrUndefinedish } from "common/Support";
import { CardFloatStyle } from "component-utils/card-types";
import type { TextSize } from "component-utils/text-types";
import { TextStyle } from "component-utils/text-types";
import * as React from "react";
import { getImageAspectRatioNumber } from "../../../lib/image-utils";
import Img from "../../smart/img/img";
import OverlayContainerView from "../overlay-container/overlay-container-view";
import { CardItemViewStyle } from "./card-item-view-style";

export interface CardItemViewProps {
    // required props
    readonly numTextLines: number;
    readonly corners: Corners;
    readonly textSize: TextSize;
    readonly textStyle: TextStyle;
    readonly size: ImageAspectRatio;
    readonly floatStyle: CardFloatStyle;
    readonly topLeftOverlay: React.ReactNode;
    readonly bottomLeftOverlay: React.ReactNode;
    readonly bottomRightOverlay: React.ReactNode;
    readonly topRightOverlay: React.ReactNode;
    readonly isHorizontal: boolean;
    readonly url: string;
    readonly hasImage: boolean;

    // optional props
    readonly title?: string;
    readonly subtitle?: string;
    readonly header?: string;
    readonly targetURL?: string;
    readonly columnWidth?: number;
    readonly className?: string;

    // actions
    readonly onClick?: (handled: boolean) => void;
}

class CardItemView extends React.PureComponent<CardItemViewProps> {
    public render(): React.ReactNode {
        const {
            title,
            subtitle,
            header,
            onClick,
            numTextLines,
            corners,
            textSize,
            textStyle,
            floatStyle,
            size,
            topLeftOverlay,
            topRightOverlay,
            bottomLeftOverlay,
            bottomRightOverlay,
            isHorizontal,
            url,
            hasImage,
            className,
        } = this.props;

        const scaler = getImageAspectRatioNumber(size);

        let styleClassName = "";

        if (textStyle === TextStyle.AllCaps) {
            styleClassName = styleClassName + " all-caps";
        }

        if (floatStyle === CardFloatStyle.Float) {
            styleClassName = styleClassName + " float";
        } else if (floatStyle === CardFloatStyle.Outline) {
            styleClassName = styleClassName + " outline";
        } else if (floatStyle === CardFloatStyle.None) {
            styleClassName = styleClassName + " no-float";
        }

        if (corners === Corners.Rounded) {
            styleClassName = styleClassName + " rounded";
        }

        if (isHorizontal) {
            styleClassName = styleClassName + " horizontal";
        }

        const hasHeader = !isEmptyOrUndefinedish(header);
        const hasTitle = !isEmptyOrUndefinedish(title);
        const needsOverlayPadding =
            (topLeftOverlay !== undefined || topRightOverlay !== undefined) && hasImage === false;

        return (
            <CardItemViewStyle
                data-test="card-item"
                numTextLines={numTextLines}
                className={className + ` ${styleClassName}`}
                textSize={textSize}
                imageScaler={scaler}
                needsOverlayPadding={needsOverlayPadding}
                onClick={onClick !== undefined ? () => onClick(false) : undefined}>
                {hasImage && (
                    <div className="card-image-container">
                        <Img
                            data-test="card-item-image"
                            alternate={<div className="image-skeleton" />}
                            altBehavior="both"
                            className="image"
                            draggable={false}
                            src={url}
                        />
                    </div>
                )}
                <div className="bottom-area">
                    {hasHeader || hasTitle ? (
                        <div className="card-title-container">
                            {hasHeader ? (
                                <div className="card-header" data-test="card-item-header">
                                    {header}
                                </div>
                            ) : null}
                            {hasTitle ? (
                                <div className="card-title" data-test="card-item-title">
                                    {title}
                                </div>
                            ) : null}
                        </div>
                    ) : null}
                    {!isEmptyOrUndefinedish(subtitle) ? (
                        <div className="card-subtitle" data-test="card-item-subtitle">
                            {subtitle}
                        </div>
                    ) : null}
                    {(bottomLeftOverlay || bottomRightOverlay) && (
                        <>
                            <div className="card-spacer" />
                            <div className="card-footer">
                                {bottomLeftOverlay && <div className="bottom-left-overlay">{bottomLeftOverlay}</div>}
                                {bottomRightOverlay && <div className="bottom-right-overlay">{bottomRightOverlay}</div>}
                            </div>
                        </>
                    )}
                </div>
                <OverlayContainerView className="overlays" topLeft={topLeftOverlay} topRight={topRightOverlay} />
            </CardItemViewStyle>
        );
    }
}

export default CardItemView;
